import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { AnalyticsEvent, AnalyticsQuery, Itinerary } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { generateSlug } from '../utils/slugGenerator';
import { Search, ChevronLeft, ChevronRight, Eye, Filter, X, Calendar, User, Globe, Activity, Clock, MapPin, RefreshCw, Play } from 'lucide-react';

interface AnalyticsTableProps {
  selectedWebsite?: string;
  dateRange: {
    start_date: string;
    end_date: string;
  };
}

const AnalyticsTable: React.FC<AnalyticsTableProps> = ({ selectedWebsite, dateRange }) => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [itineraryFilter, setItineraryFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [stepFilter, setStepFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);

  const fetchItineraries = useCallback(async () => {
    try {
      const response = await apiService.getItineraries();
      setItineraries(response.data);
    } catch (err: any) {
      console.error('Failed to fetch itineraries:', err);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const query: AnalyticsQuery = {
        website_id: selectedWebsite || undefined,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        event_type: eventTypeFilter || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        order_by: 'timestamp',
        order_direction: 'DESC',
      };

      const response = await apiService.getAnalyticsEvents(query);
      setEvents(response.events);
      setTotalEvents(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch analytics events');
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite, dateRange, eventTypeFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchItineraries();
  }, [fetchItineraries]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, eventTypeFilter, itineraryFilter, languageFilter, stepFilter, selectedWebsite, dateRange]);

  const filteredEvents = events.filter(event => {
    // Search term filter
    const matchesSearch = !searchTerm || (() => {
      const searchLower = searchTerm.toLowerCase();
      return (
        event.event_type.toLowerCase().includes(searchLower) ||
        event.website_id.toLowerCase().includes(searchLower) ||
        event.session_id?.toLowerCase().includes(searchLower) ||
        event.user_id?.toLowerCase().includes(searchLower) ||
        JSON.stringify(event.event_data).toLowerCase().includes(searchLower)
      );
    })();

    // Itinerary filter (now matches slugs instead of IDs)
    const matchesItinerary = !itineraryFilter || 
      (event.event_data && event.event_data.itinerary_id && 
       String(event.event_data.itinerary_id) === itineraryFilter);

    // Language filter
    const matchesLanguage = !languageFilter || 
      (event.event_data && event.event_data.language && 
       String(event.event_data.language).toLowerCase().includes(languageFilter.toLowerCase()));

    // Step filter
    const matchesStep = !stepFilter || 
      (event.event_data && event.event_data.step_id && 
       String(event.event_data.step_id).toLowerCase().includes(stepFilter.toLowerCase()));

    return matchesSearch && matchesItinerary && matchesLanguage && matchesStep;
  });

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: Record<string, string> = {
      page_view: 'bg-blue-100 text-blue-800',
      click: 'bg-green-100 text-green-800',
      form_submit: 'bg-purple-100 text-purple-800',
      download: 'bg-yellow-100 text-yellow-800',
      scroll: 'bg-gray-100 text-gray-800',
      hover: 'bg-indigo-100 text-indigo-800',
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800';
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewEvent = (event: AnalyticsEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const renderPagination = () => {
    // Debug info
    console.log('Pagination Debug:', {
      totalEvents,
      totalPages,
      currentPage,
      pageSize,
      eventsLength: events.length
    });

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm font-medium rounded-md ${
            i === currentPage
              ? 'bg-indigo-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalEvents)} of {totalEvents} events
          {totalPages <= 1 && <span className="ml-2 text-gray-500">(Page {currentPage} of {totalPages})</span>}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {pages}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <h3 className="text-lg font-medium text-gray-900">{t('analytics.title')}</h3>
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('analytics.searchEvents')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                className="w-full sm:w-auto pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
              >
                <option value="">{t('analytics.allEventTypes')}</option>
                <option value="page_view">{t('analytics.eventTypes.pageView')}</option>
                <option value="step_execution">{t('analytics.eventTypes.stepExecution')}</option>
                <option value="language_change">{t('analytics.eventTypes.languageChange')}</option>
                <option value="click">{t('analytics.eventTypes.click')}</option>
                <option value="form_submit">{t('analytics.eventTypes.formSubmit')}</option>
                <option value="download">{t('analytics.eventTypes.download')}</option>
                <option value="scroll">{t('analytics.eventTypes.scroll')}</option>
                <option value="hover">{t('analytics.eventTypes.hover')}</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Additional Filters Row */}
        <div className="mt-4 flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin className="h-4 w-4 text-gray-400" />
            </div>
            <select
              value={itineraryFilter}
              onChange={(e) => setItineraryFilter(e.target.value)}
              className="w-full sm:w-48 pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
            >
              <option value="">{t('analytics.allItineraries')}</option>
              {itineraries.map((itinerary) => (
                <option key={itinerary.id} value={generateSlug(itinerary.title)}>
                  {itinerary.title}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Globe className="h-4 w-4 text-gray-400" />
            </div>
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="w-full sm:w-auto pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
            >
              <option value="">{t('analytics.allLanguages')}</option>
              <option value="en">{t('analytics.languages.en')}</option>
              <option value="it">{t('analytics.languages.it')}</option>
            </select>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Activity className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Filter by Step ID..."
              value={stepFilter}
              onChange={(e) => setStepFilter(e.target.value)}
              className="w-full sm:w-48 pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.eventType')}
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.website')}
              </th>
              <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.details')}
              </th>
              <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.sessionId')}
              </th>
              <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.userId')}
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.timestamp')}
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('analytics.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEvents.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 sm:px-6 py-12 text-center text-sm text-gray-500">
                  {t('analytics.noEvents')}
                </td>
              </tr>
            ) : (
              filteredEvents.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventTypeColor(event.event_type)}`}>
                      {event.event_type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {event.website_id}
                  </td>
                  <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="space-y-1">
                      {event.event_data?.itinerary_id && (
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-3 w-3 text-blue-500" />
                          <span className="text-xs text-blue-600 font-mono">{event.event_data.itinerary_id}</span>
                        </div>
                      )}
                      {event.event_data?.language && (
                        <div className="flex items-center space-x-1">
                          <Globe className="h-3 w-3 text-green-500" />
                          <span className="text-xs text-green-600 font-mono">{event.event_data.language.toUpperCase()}</span>
                        </div>
                      )}
                      {event.event_data?.step_id && (
                        <div className="flex items-center space-x-1">
                          <Activity className="h-3 w-3 text-purple-500" />
                          <span className="text-xs text-purple-600 font-mono">{event.event_data.step_id}</span>
                        </div>
                      )}
                      {!event.event_data?.itinerary_id && !event.event_data?.language && !event.event_data?.step_id && (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.session_id ? (
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {event.session_id.substring(0, 8)}...
                      </code>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.user_id ? (
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {event.user_id.substring(0, 8)}...
                      </code>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="hidden sm:inline">{formatTimestamp(event.timestamp)}</span>
                    <span className="sm:hidden">{new Date(event.timestamp).toLocaleDateString()}</span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewEvent(event)}
                      className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                      title="View event details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Always show pagination for debugging */}
      <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
        {renderPagination()}
      </div>
      
      {/* Bottom spacing */}
      <div className="h-8"></div>

      {/* Event Details Modal */}
      {isModalOpen && selectedEvent && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            style={{ zIndex: 99999 }}
            onClick={closeModal}
          />
          {/* Modal */}
          <div 
            className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
            style={{ zIndex: 100000 }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <Activity className="h-6 w-6 text-indigo-600" />
                  <h3 className="text-lg font-medium text-gray-900">{t('analytics.eventDetails')}</h3>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Event Type */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Activity className="h-5 w-5 text-gray-600" />
                    <h4 className="font-medium text-gray-900">Event Type</h4>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getEventTypeColor(selectedEvent.event_type)}`}>
                    {selectedEvent.event_type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Globe className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium text-gray-900">Website ID</h4>
                    </div>
                    <p className="text-sm text-gray-700 font-mono">{selectedEvent.website_id}</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="h-5 w-5 text-green-600" />
                      <h4 className="font-medium text-gray-900">Timestamp</h4>
                    </div>
                    <p className="text-sm text-gray-700">{formatTimestamp(selectedEvent.timestamp)}</p>
                  </div>
                </div>

                {/* Session and User Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedEvent.session_id && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="h-5 w-5 text-purple-600" />
                        <h4 className="font-medium text-gray-900">Session ID</h4>
                      </div>
                      <code className="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {selectedEvent.session_id}
                      </code>
                    </div>
                  )}
                  
                  {selectedEvent.user_id && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-medium text-gray-900">User ID</h4>
                      </div>
                      <code className="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {selectedEvent.user_id}
                      </code>
                    </div>
                  )}
                </div>

                {/* Event Data - Structured Display */}
                {selectedEvent.event_data && Object.keys(selectedEvent.event_data).length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-4">Event Data</h4>
                    
                    {/* Display structured data for step_execution and language_change events */}
                    {(selectedEvent.event_type === 'step_execution' || selectedEvent.event_type === 'language_change') ? (
                      <div className="space-y-4">
                        {/* Itinerary ID */}
                        {selectedEvent.event_data.itinerary_id && (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <MapPin className="h-5 w-5 text-blue-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.itineraryId')}</h5>
                            </div>
                            <p className="text-sm text-gray-700 font-mono">{selectedEvent.event_data.itinerary_id}</p>
                          </div>
                        )}

                        {/* Language */}
                        {selectedEvent.event_data.language && (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Globe className="h-5 w-5 text-green-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.language')}</h5>
                            </div>
                            <p className="text-sm text-gray-700 font-mono">{selectedEvent.event_data.language.toUpperCase()}</p>
                          </div>
                        )}

                        {/* Step Information */}
                        {selectedEvent.event_data.step_id && (
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Activity className="h-5 w-5 text-purple-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.stepInformation')}</h5>
                            </div>
                            <div className="space-y-2">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">{t('analytics.eventData.stepId')}:</span> <code className="bg-gray-100 px-2 py-1 rounded text-xs">{selectedEvent.event_data.step_id}</code>
                              </p>
                              {selectedEvent.event_data.step_title && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.stepTitle')}:</span> {selectedEvent.event_data.step_title}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Language Change Specific Data */}
                        {selectedEvent.event_type === 'language_change' && (
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <RefreshCw className="h-5 w-5 text-yellow-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.languageChange')}</h5>
                            </div>
                            <div className="space-y-2">
                              {selectedEvent.event_data.from_language && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.fromLanguage')}:</span> {selectedEvent.event_data.from_language.toUpperCase()}
                                </p>
                              )}
                              {selectedEvent.event_data.to_language && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.toLanguage')}:</span> {selectedEvent.event_data.to_language.toUpperCase()}
                                </p>
                              )}
                              {selectedEvent.event_data.current_step !== undefined && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.currentStep')}:</span> {selectedEvent.event_data.current_step + 1}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Step Execution Specific Data */}
                        {selectedEvent.event_type === 'step_execution' && (
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Play className="h-5 w-5 text-indigo-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.executionDetails')}</h5>
                            </div>
                            <div className="space-y-2">
                              {selectedEvent.event_data.playback_speed && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.playbackSpeed')}:</span> {selectedEvent.event_data.playback_speed}x
                                </p>
                              )}
                              {selectedEvent.event_data.execution_timestamp && (
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">{t('analytics.eventData.executionTime')}:</span> {new Date(selectedEvent.event_data.execution_timestamp).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Timestamps */}
                        {(selectedEvent.event_data.execution_timestamp || selectedEvent.event_data.change_timestamp) && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Clock className="h-5 w-5 text-gray-600" />
                              <h5 className="font-medium text-gray-900">{t('analytics.eventData.eventTimestamp')}</h5>
                            </div>
                            <p className="text-sm text-gray-700">
                              {selectedEvent.event_data.execution_timestamp 
                                ? new Date(selectedEvent.event_data.execution_timestamp).toLocaleString()
                                : new Date(selectedEvent.event_data.change_timestamp).toLocaleString()
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Fallback to JSON display for other event types */
                      <div className="bg-gray-50 p-4 rounded-md">
                        <pre className="text-sm text-gray-700 overflow-auto">
                          {JSON.stringify(selectedEvent.event_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* Event ID */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <h4 className="font-medium text-gray-900">Event ID</h4>
                  </div>
                  <code className="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded">
                    {selectedEvent.id}
                  </code>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsTable;
